/**
 * QuickEditDialog — Modale generico per correggere campi di un record HACCP.
 *
 * Riusabile su Produzioni / Temperature / Pulizie / Scarti.
 * NON pensato per modifica massiva: serve a correggere errori di battitura
 * (lotto, temperatura, quantità, note) mantenendo i campi strutturali invariati.
 *
 * Props:
 *  - open / onClose
 *  - record: l'oggetto esistente
 *  - title: stringa header
 *  - fields: array di { key, label, type ("text"|"number"|"textarea"|"date"|"time"|"select"), options?, showIf?, helper?, placeholder? }
 *  - onSave: async (newRecord) => void. Riceve l'intero record patchato.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { IngredientiFotoGallery } from "@/components/IngredientiFotoGallery";
import { Trash2 } from "lucide-react";

export function QuickEditDialog({ open, onClose, record, title = "Modifica registrazione", fields = [], onSave, onDelete }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) setData({ ...record });
  }, [record]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md md:max-w-lg" data-testid="quick-edit-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-xs">
            Solo correzione errori di inserimento. Le modifiche restano tracciate nell&apos;audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {fields.map((f) => {
            if (f.showIf && !f.showIf(data)) return null;
            const val = data[f.key] ?? "";
            const id = `qe-${f.key}`;
            return (
              <div key={f.key}>
                <Label htmlFor={id} className="text-xs font-semibold text-slate-700">{f.label}</Label>
                {f.type === "select" ? (
                  <select
                    id={id}
                    value={val || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    data-testid={`qe-${f.key}`}
                  >
                    {f.placeholder !== undefined && <option value="">{f.placeholder || "— seleziona —"}</option>}
                    {(f.options || []).map((o) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                ) : f.type === "textarea" ? (
                  <Textarea
                    id={id}
                    value={val}
                    onChange={(e) => set(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                    rows={3}
                    className="mt-1"
                    data-testid={`qe-${f.key}`}
                  />
                ) : f.type === "number-signed" ? (
                  // numero con bottone +/- per mobile (temperature negative)
                  <div className="mt-1 flex gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        const cur = String(val || "");
                        const next = cur.startsWith("-") ? cur.slice(1) : (cur ? "-" + cur : "-");
                        set(f.key, next);
                      }}
                      className="h-9 w-9 rounded border bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm shrink-0"
                      title="Inverti segno"
                      data-testid={`qe-${f.key}-sign`}
                    >±</button>
                    <Input
                      id={id}
                      type="text"
                      inputMode="text"
                      value={val}
                      onChange={(e) => set(f.key, e.target.value.replace(/[^-0-9.,]/g, "").replace(",", "."))}
                      placeholder={f.placeholder || "—"}
                      className="text-center font-mono h-9 flex-1"
                      data-testid={`qe-${f.key}`}
                    />
                  </div>
                ) : f.type === "ingredienti_foto" ? (
                  // R53.20: galleria foto ingredienti + OCR (Plus). Modifica record salvato.
                  <div className="mt-1">
                    <IngredientiFotoGallery
                      foto={data.ingredienti_foto || []}
                      dettagli={data.ingredienti_dettagli || []}
                      onFotoChange={(foto) => setData((d) => ({ ...d, ingredienti_foto: foto }))}
                      onDettagliChange={(det) => setData((d) => ({ ...d, ingredienti_dettagli: det }))}
                      canUse={f.canUse !== false}
                      onUpgrade={f.onUpgrade || (() => {})}
                      nomeProduzione={data.prodotto || ""}
                    />
                  </div>
                ) : (
                  <Input
                    id={id}
                    type={f.type || "text"}
                    value={val}
                    onChange={(e) => set(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                    className="mt-1"
                    data-testid={`qe-${f.key}`}
                  />
                )}
                {f.helper && <p className="text-[11px] text-slate-400 mt-0.5">{f.helper}</p>}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          {onDelete && (
            <Button
              variant="ghost"
              className="sm:mr-auto text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              disabled={saving}
              data-testid="qe-delete-btn"
              onClick={async () => {
                if (!window.confirm("Eliminare definitivamente questa registrazione? L'operazione non è reversibile.")) return;
                setSaving(true);
                try {
                  await onDelete(data);
                  onClose();
                } catch { /* toast gestito dal chiamante */ } finally {
                  setSaving(false);
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-1" /> Elimina
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="qe-save-btn">
            {saving ? "Salvataggio…" : "Salva modifica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
